import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PromptingService } from 'src/app/shared/services';
import { BehaviorSubject } from 'rxjs';
import { WebsiteSocialLink } from '../../portal.model';
import { SharedPortalViewService } from '../shared-portal-view.service';

@Injectable({ providedIn: 'root' })
export class SocialLinksService {
  private linksSource = new BehaviorSubject<WebsiteSocialLink[]>([]);
  readonly links$ = this.linksSource.asObservable();

  private originalLinks: WebsiteSocialLink[] = [];
  private hasChanges = false;

  constructor(
    private toastr: ToastrService,
    private sharedPortalViewService: SharedPortalViewService,
    private prompting: PromptingService,
  ) {}

  /** Load links (usually from store or API) */
  setInitialLinks(links: WebsiteSocialLink[]): void {
    const cloned = this.deepClone(links);
    this.linksSource.next(cloned);
    this.originalLinks = cloned;
    this.hasChanges = false;
  }

  /** Add new link */
  async addLink(link: WebsiteSocialLink): Promise<void> {
    const newLink: WebsiteSocialLink = {
      ...link,
      id: this.currentLinks.length + 1,
      order: this.currentLinks.length,
    };
    let updated = [...this.currentLinks, newLink];

    await this.updateLinks(updated, true);
    this.toastr.success('Social link added successfully');
  }

  assignAndFixLinksId(links: WebsiteSocialLink[]): WebsiteSocialLink[] {
    // return links.map((l, i) => ({ ...l, id: i + 1 }));
    return links.map((l, i) => ({ ...l, id: undefined }));
  }
  get existingPlatforms(): string[] {
    return this.currentLinks.map((link) => link.platform);
  }

  /** Edit existing link */
  async updateLink(link: WebsiteSocialLink): Promise<void> {
    const index = this.currentLinks.findIndex((l) => l.id == link.id);
    if (index !== -1) {
      const links = [...this.currentLinks];
      links[index] = link;
      await this.updateLinks(links, true);
      this.toastr.success('Social link updated successfully');
    }
  }

  /** Delete with confirmation */
  async deleteLink(id: string): Promise<void> {
    const link = this.currentLinks.find((l) => l.id === id);
    if (!link) return;

    const res = await this.prompting.confirm(
      {
        title: 'Delete link?',
        html: `Are you sure you want to delete <strong>${link.platform}</strong>?`,
        confirmButtonText: 'Delete',
        isDanger: true,
      },
      'md',
    );

    if (res.isConfirmed) {
      let updated = this.currentLinks.filter((l) => l.id !== id);
      await this.updateLinks(updated, true);
      this.toastr.success('Social link removed successfully');
    }
  }

  /** Save all links to backend */
  async saveAll(): Promise<void> {
    if (!this.hasChanges) {
      // this.toastr.info('No changes to save');
      return;
    }

    const payload = {
      social_links: this.assignAndFixLinksId(this.currentLinks),
    };

    try {
      await this.sharedPortalViewService.setWebsiteIdentity(payload);
      this.originalLinks = this.deepClone(this.currentLinks);
      this.hasChanges = false;
      // this.toastr.success('Social links updated successfully');
    } catch (err) {
      console.error(err);
      // this.toastr.error('Failed to update social links.');
    }
  }

  /** Reset all changes */
  resetChanges(): void {
    this.linksSource.next(this.deepClone(this.originalLinks));
    this.hasChanges = false;
    this.toastr.info('Changes reset');
  }

  /** Reorder links (drag & drop) */
  async reorderLinks(updated: WebsiteSocialLink[]) {
    const reordered = updated.map((l, i) => ({ ...l, order: i }));
    await this.updateLinks(reordered, true);
  }

  /** ────────────────────────────────
   *  Internal Utilities
   *  ──────────────────────────────── */
  private async updateLinks(links: WebsiteSocialLink[], markChanged = false) {
    links = this.assignAndFixLinksId(links);
    this.linksSource.next(links);
    if (markChanged) this.hasChanges = true;
    await this.saveAll();
  }

  private get currentLinks(): WebsiteSocialLink[] {
    return this.linksSource.getValue();
  }

  private generateUniqueId(): string {
    return `link_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private deepClone<T>(obj: T): T {
    return JSON.parse(JSON.stringify(obj));
  }
}
